import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { RegExpMarker } from "abreact/grid/generic/RegExpMarker";
import { AbHamburgerMenuButton } from "abreact/incubator/ui/abVSequenceEditor/AbHamburgerMenuButton";
import { AbCodeEditor } from "abreact/incubator/ui/codeeditor/AbCodeEditor";
import { CodeEditorMachine } from "abreact/incubator/ui/codeeditor/CodeEditorMachine";
import { AbErrorBoundary } from "abreact/incubator/ui/component/AbErrorBoundary";
import { AbMarkdownExtensions } from "abreact/input/markdown/AbMarkdownExtensions";
import { AbToolbar } from "abreact/layout/toolbar/AbToolbar";
import { AbButton } from "abreact/misc/AbButton";
import { AbColorPicker } from "abreact/misc/AbColorPicker";
import { useLazyRef } from "abreact/utils/AbHooks";
import * as AbWebResources from "abweb/generated/locale/AbWebResources";
import { ClipboardUtils } from "abweb/utils/clipboard/ClipboardUtils";
import { URLUtils } from "abweb/utils/URLUtils";
import React from "react";
class PartialComponent {
  constructor() {
    _defineProperty(this, "component", void 0);
    // react component being built.  Default: fragment.
    _defineProperty(this, "extension", void 0);
    // link extensions.
    _defineProperty(this, "props", {});
    // props for the component being built.
    _defineProperty(this, "contents", []);
  } // children of the component.
}
const AbCodeEditorMarkdown = props => {
  const machineRef = useLazyRef(() => {
    return new CodeEditorMachine();
  });
  const trimmedValue = props.value.endsWith("\n") ? props.value.slice(0, -1) : props.value;
  const [lineWrap, setLineWrap] = React.useState(false);
  const [showGutter, setShowGutter] = React.useState(true);
  const [numberOfLines, setNumberOfLines] = React.useState(0);
  React.useEffect(() => {
    machineRef.current.value = trimmedValue;
    machineRef.current.cursorPos = -1;
    setNumberOfLines(trimmedValue.split("\n").length);
  }, [machineRef, trimmedValue]);

  // Toolbar
  const copyButton = /*#__PURE__*/React.createElement(AbButton, {
    key: 1,
    icon: "absdk:copy",
    label: AbWebResources.copy(),
    onClick: () => ClipboardUtils.simpleCopyToSystemClipboard(props.value)
  });
  const hamburgerMenu = /*#__PURE__*/React.createElement(AbHamburgerMenuButton, {
    key: 2,
    menuItems: [{
      label: AbReactResources.markdownContent_softWrap(),
      icon: lineWrap ? "absdk:check" : "absdk:transparent",
      onClick: () => setLineWrap(!lineWrap)
    }, {
      label: AbReactResources.markdownContent_lineNumbers(),
      icon: showGutter ? "absdk:check" : "absdk:transparent",
      onClick: () => setShowGutter(!showGutter)
    }]
  });
  const fixedGroup = {
    items: [copyButton, hamburgerMenu]
  };
  if (props.customCodeToolbarFixedGroup) {
    fixedGroup.items = [...props.customCodeToolbarFixedGroup(props.language).items, ...fixedGroup.items];
  }

  // Syntax-highlighted code
  const extension = props.language ? AbMarkdownExtensions.markdownCodeEditorExtensions.get(props.language.toLowerCase()) : undefined;
  let renderedCode = extension ? extension.render({
    value: trimmedValue,
    language: props.language,
    readOnly: true,
    lineWrap: lineWrap,
    showGutter: showGutter
  }) : /*#__PURE__*/React.createElement(AbCodeEditor, {
    width: "100%",
    height: numberOfLines + "lh",
    machine: machineRef.current,
    lineWrap: lineWrap,
    options: {
      showGutter: showGutter
    },
    mode: props.language,
    readonly: true
  });
  if (!extension || extension.includesToolbar) {
    return /*#__PURE__*/React.createElement("div", {
      className: "ab-markdown-code"
    }, /*#__PURE__*/React.createElement(AbToolbar, {
      groups: [{
        items: [props.language]
      }],
      fixedGroup: fixedGroup
    }), renderedCode);
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, renderedCode);
};
export class AbMarkdownReactRenderer {
  render(tokens, _options, _env) {
    this.key = 0;
    let reactNodes = this.renderTokenArray(tokens);
    return /*#__PURE__*/React.createElement("div", null, reactNodes);
  }
  addToStack(component, props) {
    let partialComponent = new PartialComponent();
    partialComponent.component = component;
    if (props) partialComponent.props = props;
    this.stack.push(partialComponent);
    return partialComponent;
  }

  // we have a link.  Check the protocol. If it matches an extension, use its render() method.
  handleLink(token) {
    const url = token.href;
    const protocol = URLUtils.getProtocol(url);
    const extension = protocol ? AbMarkdownExtensions.markdownLinkExtensions.get(protocol) : undefined;
    if (extension) {
      let partialComponent = new PartialComponent();
      partialComponent.extension = extension;
      partialComponent.props = {
        url
      };
      this.stack.push(partialComponent);
      return partialComponent;
    }
    // no extension matches; use the default link renderer: <a href>
    return this.addToStack("a", {
      href: token.href,
      target: "_blank"
    });
  }
  handlePerson(token) {
    if (!this.personExtension) {
      // no extension loaded, so just start an <a> href with protocol "person:".
      return this.addToStack("a", {
        href: "person:" + token.content.id,
        target: "_blank"
      });
    }
    const id = token.content.id;
    const displayName = token.content.displayName;
    let partialComponent = new PartialComponent();
    partialComponent.extension = this.personExtension;
    partialComponent.props = {
      id,
      displayName
    };
    this.stack.push(partialComponent);
    return partialComponent;
  }
  handleEzGraph(token) {
    if (!this.ezgraphExtension) {
      // no extension loaded, so just start an <a> href with protocol "ezgraph:".
      return this.addToStack("a", {
        href: "ezgraph:" + token.content.id,
        target: "_blank"
      });
    }
    const id = token.content.id;
    const displayName = token.content.displayName;
    let partialComponent = new PartialComponent();
    partialComponent.extension = this.ezgraphExtension;
    partialComponent.props = {
      id,
      displayName
    };
    this.stack.push(partialComponent);
    return partialComponent;
  }
  renderTokenArray(tokens) {
    let topOfStack = new PartialComponent();
    this.stack.push(topOfStack);
    for (let token of tokens) {
      if (AbMarkdownReactRenderer.colorTokens.includes(token.type)) {
        // looking at a color token?
        let parts = token.type.split("_");
        let newColor = parts[1];
        if (newColor.indexOf("-") >= 0) {
          // if it's a color like "blue-700", make a var reference.
          newColor = `var(--ab-global-color-${newColor})`;
        }
        if (parts[2] === "open") {
          topOfStack = this.addToStack("span", parts[0] === "color" ? {
            style: {
              color: newColor
            }
          } : {
            style: {
              backgroundColor: newColor
            }
          });
        }
        if (parts[2] === "close") {
          let oldTop = this.stack.pop();
          topOfStack = this.stack[this.stack.length - 1]; // new top
          if (!topOfStack) {
            // shouldn't happen, but be safe
            topOfStack = new PartialComponent();
            this.stack.push(topOfStack);
          }
          let span = /*#__PURE__*/React.createElement("span", {
            key: this.key++,
            ...oldTop?.props
          }, oldTop?.contents);
          topOfStack.contents.push(span);
        }
        continue;
      }
      switch (token.type) {
        case "text":
          if (this.containerComponent.gridFindRegExp && this.containerComponent.props.isHighlighted) {
            topOfStack.contents.push(/*#__PURE__*/React.createElement(RegExpMarker, {
              key: this.key++,
              regExp: this.containerComponent.gridFindRegExp,
              text: token.content,
              returnFragment: true
            }));
          } else {
            topOfStack.contents.push(token.content);
          }
          break;
        case "inline":
          let blockContentToken = token;
          if (!blockContentToken.children) continue; // empty block
          topOfStack.contents.push(...this.renderTokenArray(blockContentToken.children)); // recurse
          break;
        case "fence":
          if (this.codeBlocksUsePre) {
            topOfStack.contents.push(/*#__PURE__*/React.createElement("pre", null, /*#__PURE__*/React.createElement("code", null, token.content)));
          } else {
            let fenceToken = token;
            topOfStack.contents.push(/*#__PURE__*/React.createElement(AbCodeEditorMarkdown, {
              key: this.key++,
              language: fenceToken.params,
              value: fenceToken.content,
              customCodeToolbarFixedGroup: this.customCodeToolbarFixedGroup
            }));
          }
          break;
        case "link_open":
          topOfStack = this.handleLink(token);
          break;
        case "person_open":
          topOfStack = this.handlePerson(token);
          break;
        case "ezgraph_open":
          topOfStack = this.handleEzGraph(token);
          break;
        case "heading_open":
          let hLevel = token.hLevel;
          topOfStack = this.addToStack("h" + hLevel, {
            className: AbMarkdownReactRenderer.headingClassName[hLevel]
          });
          break;
        case "paragraph_open":
        case "bullet_list_open":
        case "ordered_list_open":
        case "blockquote_open":
        case "list_item_open":
        case "strong_open":
        case "em_open":
        case "ins_open":
        case "del_open":
        case "mark_open":
        case "table_open":
        case "td_open":
        case "th_open":
        case "thead_open":
        case "tr_open":
        case "tbody_open":
          topOfStack = this.addToStack(AbMarkdownReactRenderer.components[token.type]);
          break;
        case "paragraph_close":
        case "bullet_list_close":
        case "ordered_list_close":
        case "heading_close":
        case "blockquote_close":
        case "list_item_close":
        case "strong_close":
        case "em_close":
        case "ins_close":
        case "del_close":
        case "link_close":
        case "person_close":
        case "ezgraph_close":
        case "mark_close":
        case "table_close":
        case "td_close":
        case "th_close":
        case "thead_close":
        case "tr_close":
        case "tbody_close":
          let partialComponent = this.stack.pop();
          topOfStack = this.stack[this.stack.length - 1]; // new top
          if (!topOfStack) {
            // shouldn't happen, but be safe.
            topOfStack = new PartialComponent();
            this.stack.push(topOfStack);
          }
          if (partialComponent) {
            if (partialComponent.component) {
              topOfStack.contents.push(/*#__PURE__*/React.createElement(partialComponent.component, {
                key: this.key++,
                ...partialComponent.props
              }, partialComponent.contents));
            } else if (partialComponent.extension) {
              // Use an errorBoundary to make sure the extension's rendering doesn't hurt the rest of the rendering
              topOfStack.contents.push(/*#__PURE__*/React.createElement(AbErrorBoundary, {
                key: this.key++
              }, partialComponent.extension.render(partialComponent.props, partialComponent.contents)));
            } else if (partialComponent.contents.length > 0) {
              // no component type, no element, no extension, so add a fragment
              topOfStack.contents.push(/*#__PURE__*/React.createElement(React.Fragment, {
                key: this.key++
              }, partialComponent.contents));
            }
          }
          break;
        case "code":
          topOfStack.contents.push(/*#__PURE__*/React.createElement("code", {
            key: this.key++
          }, token.content));
          break;
        case "image":
          let imageToken = token;
          const questionMarkPos = imageToken.src.indexOf("?");
          let width = undefined;
          let height = undefined;
          if (questionMarkPos >= 0) {
            // yes, there are query parameters
            try {
              // ABSDK-4921: Bad urls throw
              const queryString = imageToken.src.substring(questionMarkPos + 1);
              const queryStringParameters = URLUtils.queryStringToObject(queryString);
              width = queryStringParameters.width;
              height = queryStringParameters.height;
            } catch (e) {/* empty */
            }
          } else if (imageToken.src.startsWith("data:")) {
            const matchWidth = imageToken.src.match(/width=([^;]*);/);
            if (matchWidth) width = matchWidth[1];
            const matchHeight = imageToken.src.match(/height=([^;]*);/);
            if (matchHeight) height = matchHeight[1];
          }
          // eslint-disable-next-line react/forbid-elements
          topOfStack.contents.push(/*#__PURE__*/React.createElement("img", {
            key: this.key++,
            src: imageToken.src,
            alt: imageToken.alt,
            width: width,
            height: height
          }));
          break;
        case "softbreak":
          topOfStack.contents.push("\n");
          break;
        case "hardbreak":
          topOfStack.contents.push(/*#__PURE__*/React.createElement("br", null));
          break;
        case "hr":
          topOfStack.contents.push(/*#__PURE__*/React.createElement("hr", null));
          break;
        default:
          console.error(`AbMarkdownReactRenderer: cannot handle token of type ${token.type}`);
      }
    }
    let oldTop = this.stack.pop();
    if (!oldTop) {
      throw new Error("AbMarkdownReactRenderer: null stack pop");
    }
    return oldTop.contents;
  }
  constructor(containerComponent, customCodeToolbarFixedGroup, codeBlocksUsePre) {
    _defineProperty(this, "stack", []);
    _defineProperty(this, "key", 0);
    _defineProperty(this, "personExtension", void 0);
    _defineProperty(this, "ezgraphExtension", void 0);
    _defineProperty(this, "containerComponent", void 0);
    _defineProperty(this, "customCodeToolbarFixedGroup", void 0);
    _defineProperty(this, "codeBlocksUsePre", void 0);
    AbMarkdownReactRenderer.colorTokens = AbColorPicker.getColorTokens();
    try {
      this.personExtension = AbMarkdownExtensions.markdownPersonExtension.get();
    } catch (e) {
      this.personExtension = undefined;
    }
    try {
      this.ezgraphExtension = AbMarkdownExtensions.markdownLinkExtensions.get("");
    } catch (e) {
      this.ezgraphExtension = undefined;
    }
    this.containerComponent = containerComponent;
    this.customCodeToolbarFixedGroup = customCodeToolbarFixedGroup;
    this.codeBlocksUsePre = codeBlocksUsePre;
  }
}
_defineProperty(AbMarkdownReactRenderer, "components", {
  blockquote_open: "blockquote",
  bullet_list_open: "ul",
  del_open: "del",
  em_open: "em",
  ins_open: "ins",
  link_open: "a",
  list_item_open: "li",
  mark_open: "mark",
  ordered_list_open: "ol",
  paragraph_open: "p",
  strong_open: "strong",
  table_open: "table",
  td_open: "td",
  th_open: "th",
  thead_open: "thead",
  tr_open: "tr",
  tbody_open: "tbody"
});
_defineProperty(AbMarkdownReactRenderer, "headingClassName", {
  1: "ab-font-heading-large",
  2: "ab-font-heading",
  3: "ab-font-heading-small",
  4: "ab-font-heading-xsmall",
  5: "ab-font-heading-2xsmall",
  6: "ab-font-heading-3xsmall",
  7: undefined,
  8: undefined,
  9: undefined
});
_defineProperty(AbMarkdownReactRenderer, "colorTokens", void 0);
//# sourceMappingURL=AbMarkdownReactRenderer.js.map
